<script>
// utils and composables
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { parseBoolean } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';

// components
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    NextButton,
    MfaVerification,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  emits: [],
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      // We need to initialize the component with any
      // properties that will be used in it
      credentials: {
        email: '',
        password: '',
        captchaCode: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
      // 验证码相关
      captcha: {
        sessionId: '',
        imageData: '',
        loading: false,
      },
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    showGoogleOAuth() {
      return Boolean(window.chatwootConfig.googleOAuthClientId);
    },
    showSignupLink() {
      return parseBoolean(window.chatwootConfig.signupEnabled);
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      // Use a method to get the translated text to avoid dynamic key warning
      const translatedMessage = this.getTranslatedMessage(messageKey);
      useAlert(translatedMessage);
      // wait for idle state
      this.requestIdleCallbackPolyfill(() => {
        // Remove the error query param from the url
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }

    // 生成验证码
    this.generateCaptcha();
  },
  methods: {
    getTranslatedMessage(key) {
      // Avoid dynamic key warning by handling each case explicitly
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        // Fallback for safari
        // Using a delay of 0 allows the callback to be executed asynchronously
        // in the next available event loop iteration, similar to requestIdleCallback
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      // Detects impersonation mode via URL and sets a session flag to prevent user settings changes during impersonation.
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    async generateCaptcha() {
      this.captcha.loading = true;
      try {
        const response = await fetch('/api/v1/captcha/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Failed to generate captcha');
        }

        const data = await response.json();
        this.captcha.sessionId = data.session_id;
        this.captcha.imageData = data.image;

        console.log('[Captcha] Generated successfully');
      } catch (error) {
        console.error('[Captcha] Generation error:', error);
        useAlert('Failed to load captcha. Please refresh the page.');
      } finally {
        this.captcha.loading = false;
      }
    },
    refreshCaptcha() {
      this.credentials.captchaCode = '';
      this.generateCaptcha();
    },
    async submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
        captcha_session_id: this.captcha.sessionId,
        captcha_code: this.credentials.captchaCode,
      };

      login(credentials)
        .then(result => {
          // Check if MFA is required
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }

          // Captcha failure: show error and refresh captcha without redirecting
          if (result?.captchaFailed) {
            this.loginApi.showLoading = false;
            this.loginApi.hasErrored = true;
            this.showAlertMessage(result.message);
            this.refreshCaptcha();
            return;
          }

          this.handleImpersonation();
        })
        .catch(response => {
          // Authentication failure (wrong email/password etc.) - reset URL params
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );

          // 登录失败后刷新验证码
          this.refreshCaptcha();
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleMfaVerified() {
      // MFA verification successful, continue with login
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      // User cancelled MFA, reset state
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
  },
};
</script>

<template>
  <main class="wlab-login-container">
    <!-- Background gradient at bottom -->
    <div class="wlab-bg-gradient" />

    <!-- Centered Form -->
    <div class="wlab-center-section">
      <!-- Logo -->
      <div class="wlab-logo-container">
        <img
          src="../../../dashboard/assets/images/wlab-logo.png"
          alt="WLAB"
          class="wlab-logo"
        />
      </div>

      <h1 class="wlab-welcome-text">Welcome Back</h1>

      <!-- Login Card -->
      <div class="wlab-login-card">
        <!-- MFA Verification -->
        <div v-if="mfaRequired" class="wlab-mfa-container">
          <MfaVerification
            :mfa-token="mfaToken"
            @verified="handleMfaVerified"
            @cancel="handleMfaCancel"
          />
        </div>

        <!-- Login Form -->
        <div
          v-else
          class="wlab-form-container"
          :class="{ 'shake-animation': loginApi.hasErrored }"
        >
          <div v-if="!email">
            <form class="wlab-form" @submit.prevent="submitFormLogin">
              <div class="wlab-form-group">
                <label class="wlab-label">Email</label>
                <FormInput
                  v-model="credentials.email"
                  name="email_address"
                  type="text"
                  data-testid="email_input"
                  :tabindex="1"
                  required
                  placeholder="Your email address"
                  :has-error="v$.credentials.email.$error"
                  @input="v$.credentials.email.$touch"
                />
              </div>

              <div class="wlab-form-group">
                <div class="wlab-label-row">
                  <label class="wlab-label">Password</label>
                </div>
                <FormInput
                  v-model="credentials.password"
                  type="password"
                  name="password"
                  data-testid="password_input"
                  required
                  :tabindex="2"
                  placeholder="Password"
                  :has-error="v$.credentials.password.$error"
                  @input="v$.credentials.password.$touch"
                />
              </div>

              <!-- 验证码 -->
              <div class="wlab-form-group">
                <label class="wlab-label">Verification Code</label>
                <div class="wlab-captcha-container">
                  <div class="wlab-captcha-image-wrapper">
                    <img
                      v-if="captcha.imageData && !captcha.loading"
                      :src="captcha.imageData"
                      alt="Captcha"
                      class="wlab-captcha-image"
                    />
                    <div v-else class="wlab-captcha-loading">
                      <Spinner color-scheme="primary" size="small" />
                    </div>
                    <button
                      type="button"
                      class="wlab-captcha-refresh"
                      :disabled="captcha.loading"
                      @click="refreshCaptcha"
                    >
                      ↻
                    </button>
                  </div>
                  <FormInput
                    v-model="credentials.captchaCode"
                    type="text"
                    name="captcha"
                    data-testid="captcha_input"
                    required
                    :tabindex="3"
                    placeholder="Enter 4 digits"
                    maxlength="4"
                    autocomplete="off"
                  />
                </div>
              </div>

              <NextButton
                lg
                type="submit"
                data-testid="submit_button"
                class="wlab-submit-btn"
                :tabindex="3"
                label="Sign In"
                icon="arrow-up-right"
                :disabled="loginApi.showLoading"
                :is-loading="loginApi.showLoading"
              />
            </form>

            <p v-if="showSignupLink" class="wlab-signup-link">
              Don't have an account?
              <router-link to="auth/signup" class="wlab-link">
                Sign Up
              </router-link>
            </p>
          </div>
          <div v-else class="wlab-loading">
            <Spinner color-scheme="primary" size="" />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="wlab-footer">
      <p class="wlab-copyright">
        Copyright © 2026 WLAB™. All rights reserved.
      </p>
      <div class="wlab-footer-links">
        <a href="#" class="wlab-footer-link">Terms of Use</a>
        <a href="#" class="wlab-footer-link">Privacy Policy</a>
      </div>
    </footer>
  </main>
</template>

<style scoped>
/* Main Container */
.wlab-login-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  width: 100%;
  font-family:
    'Excon',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  overflow: hidden;
  background: #ffffff;
  position: relative;
}

/* Background Gradient at Bottom */
.wlab-bg-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70%;
  background-image: url('../../../dashboard/assets/images/login-background.png');
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  pointer-events: none;
  z-index: 0;
}

/* Center Section */
.wlab-center-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

/* Logo */
.wlab-logo-container {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeInDown 0.6s ease-out;
}

.wlab-logo {
  height: 42px;
  width: auto;
}

/* Welcome Text */
.wlab-welcome-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: #000000;
  margin-bottom: 1rem;
  text-align: center;
  animation: fadeInDown 0.6s ease-out 0.1s both;
}

/* Login Card */
.wlab-login-card {
  width: 100%;
  max-width: 400px;
  background: #ffffff;
  border-radius: 16px;
  padding: 1.5rem 2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  animation: fadeInUp 0.6s ease-out 0.2s both;
}

/* Social Title */
.wlab-social-title {
  text-align: center;
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 1.25rem;
}

/* Social Buttons */
.wlab-social-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.wlab-social-btn {
  width: 100px;
  height: 48px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.wlab-social-btn:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.wlab-social-icon {
  width: 20px;
  height: 20px;
}

/* MFA Container */
.wlab-mfa-container {
  margin-bottom: 1rem;
}

/* Form Container */
.wlab-form-container {
  margin-bottom: 0;
}

/* Form */
.wlab-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Form Group */
.wlab-form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.wlab-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wlab-label {
  font-size: 0.813rem;
  font-weight: 500;
  color: #374151;
}

/* Forgot Password Link */
.wlab-forgot-link {
  font-size: 0.75rem;
  color: #f97316;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
}

.wlab-forgot-link:hover {
  color: #ea580c;
}

/* Captcha Container */
.wlab-captcha-container {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.wlab-captcha-image-wrapper {
  position: relative;
  flex-shrink: 0;
  width: 130px;
  height: 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wlab-captcha-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wlab-captcha-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.wlab-captcha-refresh {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.wlab-captcha-refresh:hover:not(:disabled) {
  background: #ffffff;
  transform: rotate(180deg);
}

.wlab-captcha-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wlab-captcha-container input {
  flex: 1;
  text-align: center;
  letter-spacing: 0.5em;
  font-size: 1rem;
  font-weight: 600;
}

/* Submit Button */
.wlab-submit-btn {
  margin-top: 0.75rem;
  width: 100%;
  height: 48px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.938rem;
  background: #f97316 !important;
  color: #ffffff !important;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(249, 115, 22, 0.3);
}

.wlab-submit-btn:hover:not(:disabled) {
  background: #ea580c !important;
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.4);
}

.wlab-submit-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.wlab-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Divider */
.wlab-divider {
  position: relative;
  text-align: center;
  margin: 1.25rem 0;
}

.wlab-divider::before,
.wlab-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 35%;
  height: 1px;
  background: #e5e7eb;
}

.wlab-divider::before {
  left: 0;
}

.wlab-divider::after {
  right: 0;
}

.wlab-divider span {
  display: inline-block;
  padding: 0 0.75rem;
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 400;
  background: #ffffff;
}

/* Signup Link */
.wlab-signup-link {
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 400;
}

.wlab-link {
  color: #f97316;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  margin-left: 0.25rem;
}

.wlab-link:hover {
  color: #ea580c;
  text-decoration: underline;
}

/* Loading */
.wlab-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
}

/* Footer */
.wlab-footer {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 0.75rem;
  flex-shrink: 0;
}

.wlab-copyright {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.wlab-footer-links {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
}

.wlab-footer-link {
  font-size: 0.75rem;
  color: #374151;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.wlab-footer-link:hover {
  color: #000000;
}

/* Animations */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Shake Animation for Errors */
.shake-animation {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-6px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(6px);
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .wlab-center-section {
    padding: 1.5rem;
  }

  .wlab-login-card {
    padding: 1.5rem;
    border-radius: 12px;
  }

  .wlab-social-buttons {
    gap: 0.75rem;
  }

  .wlab-social-btn {
    width: 80px;
    height: 44px;
  }

  .wlab-welcome-text {
    font-size: 1.125rem;
  }
}

/* Input Field Styles Override */
:deep(input[type='text']),
:deep(input[type='password']),
:deep(input[type='email']) {
  width: 100%;
  height: 44px;
  padding: 0 1rem;
  font-size: 0.875rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff !important;
  color: #000000;
  transition: all 0.2s ease;
  font-family: 'Excon', sans-serif;
  font-weight: 400;
}

:deep(input[type='text']:focus),
:deep(input[type='password']:focus),
:deep(input[type='email']:focus) {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #ffffff !important;
}

:deep(input[type='text']::placeholder),
:deep(input[type='password']::placeholder),
:deep(input[type='email']::placeholder) {
  color: #9ca3af;
  font-weight: 300;
}

/* Remove autofill blue background - keep white */
:deep(input:-webkit-autofill),
:deep(input:-webkit-autofill:hover),
:deep(input:-webkit-autofill:focus),
:deep(input:-webkit-autofill:active) {
  -webkit-box-shadow: 0 0 0 50px #ffffff inset !important;
  box-shadow: 0 0 0 50px #ffffff inset !important;
  -webkit-text-fill-color: #000000 !important;
  background-color: #ffffff !important;
  background: #ffffff !important;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
